import { useState, useEffect, useContext } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import {
  Box,
  Paper,
  TextField,
  Tooltip,
  IconButton,
  Grid,
  MenuItem,
  Typography,
  Divider,
  Autocomplete,
} from "@mui/material"
import { createTheme, ThemeProvider } from "@mui/material/styles"
import Header from "../../../layouts/Header"
import BackIcon from "../../../components/BackIcon"
import CustomBackdrop from "../../../components/CustomBackdrop"
import { useMutation, useQuery, api, showWarning } from "../../../api"
import { GlobalContext } from "../../../contexts/GlobalContext"
import getIconComponent from "../../utils/getIconComponent"
import Save from "@mui/icons-material/Save"

const theme = createTheme({
  palette: { primary: { main: "#196C87" }, secondary: { main: "#2E7D32" } },
})

const StyledRoot = {
  width: "100%",
  maxWidth: "1200px",
  margin: "0 auto",
  padding: "20px",
  backgroundColor: "#f5f7fa",
  borderRadius: "12px",
  boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
}

const EditarSubTiposPPE = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { selectedMenuInfo } = useContext(GlobalContext)

  const [formData, setFormData] = useState({
    tipcodigo: "",
    tipdescri: "",
    gencodigo: "",
    gendescri: "",
    genstatus: "A",
    tipplacas: 0,
    tipcodcatastral: 0,
    tipsuperficie: 0,
    tipserie: 0,
    tipdeprecia: 0,
  })

  // --- LÓGICA DEFENSIVA APLICADA ---
  const { data: rawTiposPadre, isLoading: isLoadingTipos } = useQuery({
    queryKey: ["listaTiposPadrePPE"],
    queryFn: async () => {
      try {
        const response = await api.get("/TiposPPE/getListaTiposPPE")
        const dataExtraida = response?.data?.data || response?.data || response || []
        return dataExtraida
      } catch (error) {
        console.error("Error al cargar Tipos Padre:", error)
        return []
      }
    },
  })

  // Protección estricta: garantizamos que sea un Array
  const tiposValidados = Array.isArray(rawTiposPadre)
    ? rawTiposPadre
    : Array.isArray(rawTiposPadre?.data)
      ? rawTiposPadre.data
      : []

  // Mapeamos al formato exacto {id, label} que exige el Autocomplete
  const listaTiposPadre = tiposValidados.map((item) => ({
    id: item.tipcodigo || item.id,
    label: item.label || `${item.tipcodigo} - ${item.tipdescri || ""}`,
  }))
  // --- FIN LÓGICA DEFENSIVA ---

  useEffect(() => {
    if (location.state) setFormData((prev) => ({ ...prev, ...location.state }))
    else navigate("/home/dashboard/SubTiposPPE")
  }, [location, navigate])

  const { mutateAsync: SaveEdicionSubTipo, isPending: isSavingEdicion } = useMutation({
    queryKey: ["isUpdatingSubTipo"],
    fn: async (data) => {
      const response = await api.post("/SubTiposPPE/updateSubTiposPPE", data)
      return response.data
    },
    showError: "modal",
    showSuccess: "toast",
    onSuccess: () => navigate(-1),
  })

  const handleInputChange = (field, value) => {
    let finalValue = value
    if (field === "gendescri") finalValue = value.toUpperCase().slice(0, 100)
    else if (["tipplacas", "tipcodcatastral", "tipsuperficie", "tipserie", "tipdeprecia"].includes(field))
      finalValue = Number(value)

    setFormData((prev) => ({ ...prev, [field]: finalValue }))
  }

  const handleSubmit = async (e) => {
    if (e) e.preventDefault()
    if (!formData.gendescri.trim()) return showWarning("La descripción es obligatoria")
    await SaveEdicionSubTipo({ ...formData })
  }

  const actualizarAction = selectedMenuInfo?.data?.barraAcciones?.find(
    (a) => a?.acccaption === "ACTUALIZAR" || a?.acccaption === "GUARDAR" || a?.acccaption === "EDITAR",
  )
  const toolbarActions = actualizarAction
    ? [
        {
          label: actualizarAction.acccaption,
          key: actualizarAction.acccaption,
          icon: getIconComponent(actualizarAction.accnameicono, actualizarAction.acctipoico),
        },
      ]
    : [{ label: "Actualizar", key: "ACTUALIZAR", icon: <Save /> }]

  return (
    <ThemeProvider theme={theme}>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <BackIcon />
        <Box>
          {toolbarActions.map((action) => (
            <Tooltip title={action.label} key={action.key}>
              <IconButton
                onClick={handleSubmit}
                disabled={isSavingEdicion || isLoadingTipos}
                sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1 }}
              >
                {action.icon}
              </IconButton>
            </Tooltip>
          ))}
        </Box>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 30px 30px 30px",
            fontSize: "25px",
          }}
        >
          <b>Editar Sub Tipo: {formData.gencodigo}</b>
        </div>
        <CustomBackdrop isLoading={isSavingEdicion || isLoadingTipos} />
        <Box sx={StyledRoot}>
          <Paper
            elevation={3}
            sx={{ p: 3, mb: 3, borderRadius: 3, background: "white" }}
            component="form"
            onSubmit={handleSubmit}
          >
            <Typography variant="h6" color="primary" gutterBottom>
              Datos Generales
            </Typography>
            <Grid container spacing={3} mb={3}>
              <Grid item xs={12} sm={4}>
                <Autocomplete
                  disabled // Bloqueado porque es parte de la Llave Primaria
                  options={listaTiposPadre}
                  getOptionLabel={(option) => option.label || ""}
                  // LÓGICA DE FOCO FALLBACK
                  value={
                    listaTiposPadre.find((c) => c.id === formData.tipcodigo) ||
                    (formData.tipcodigo ? { id: formData.tipcodigo, label: `${formData.tipcodigo}` } : null)
                  }
                  onChange={(event, newValue) => handleInputChange("tipcodigo", newValue ? newValue.id : "")}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  renderInput={(params) => (
                    <TextField {...params} label="Tipo de Activo Padre *" InputLabelProps={{ shrink: true }} />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <TextField
                  disabled
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  label="Cód. Sub Tipo *"
                  value={formData.gencodigo}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  label="Descripción *"
                  value={formData.gendescri}
                  onChange={(e) => handleInputChange("gendescri", e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <TextField
                  select
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  label="Estado"
                  value={formData.genstatus}
                  onChange={(e) => handleInputChange("genstatus", e.target.value)}
                >
                  <MenuItem value="A">ACTIVO</MenuItem>
                  <MenuItem value="I">INACTIVO</MenuItem>
                </TextField>
              </Grid>
            </Grid>
            <Divider sx={{ mb: 3 }} />
            <Typography variant="h6" color="primary" gutterBottom>
              Indicadores y Controles Adicionales
            </Typography>
            <Grid container spacing={3} mb={3}>
              <Grid item xs={12} sm={2}>
                <TextField
                  select
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  label="Usa Placas"
                  value={formData.tipplacas}
                  onChange={(e) => handleInputChange("tipplacas", e.target.value)}
                >
                  <MenuItem value={1}>SÍ</MenuItem>
                  <MenuItem value={0}>NO</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={2}>
                <TextField
                  select
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  label="Usa Cód. Catastral"
                  value={formData.tipcodcatastral}
                  onChange={(e) => handleInputChange("tipcodcatastral", e.target.value)}
                >
                  <MenuItem value={1}>SÍ</MenuItem>
                  <MenuItem value={0}>NO</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={2}>
                <TextField
                  select
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  label="Usa Superficie"
                  value={formData.tipsuperficie}
                  onChange={(e) => handleInputChange("tipsuperficie", e.target.value)}
                >
                  <MenuItem value={1}>SÍ</MenuItem>
                  <MenuItem value={0}>NO</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={2}>
                <TextField
                  select
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  label="Usa Serie"
                  value={formData.tipserie}
                  onChange={(e) => handleInputChange("tipserie", e.target.value)}
                >
                  <MenuItem value={1}>SÍ</MenuItem>
                  <MenuItem value={0}>NO</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={2}>
                <TextField
                  select
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  label="Deprecia"
                  value={formData.tipdeprecia}
                  onChange={(e) => handleInputChange("tipdeprecia", e.target.value)}
                >
                  <MenuItem value={1}>SÍ</MenuItem>
                  <MenuItem value={0}>NO</MenuItem>
                </TextField>
              </Grid>
            </Grid>
          </Paper>
        </Box>
      </div>
    </ThemeProvider>
  )
}

export default EditarSubTiposPPE
